import React, { useState, useMemo, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useLazyQuery, useMutation } from '@apollo/client'
import { Modal, Radio, Select, Button, Alert, message } from 'antd'
import _ from 'lodash'

import CompanyFormContainer from '../Companies/CompanyFormContainer'
import { formatMutationErrors } from './format'
import {
    COMPANY_TYPEAHEAD_QUERY,
    UPDATE_CONTACT_COMPANIES_MUTATION
} from './queries'

const EXISTING = 'existing'
const NEW = 'new'

const companyLabel = (c) =>
    [c.name, c.taxId ? `เลขภาษี: ${c.taxId}` : ''].filter(Boolean).join(' · ')

// Modal for attaching a company to a customer from the dashboard holdings view.
// Two modes: pick an existing company (union with the customer's current
// companies) or create a new company then attach it.
const AddCompanyModal = ({ customer, visible, onClose, onSuccess }) => {
    const [mode, setMode] = useState(EXISTING)
    const [selected, setSelected] = useState(null)

    const customerNo = customer && customer.customerNo
    const existingIds = useMemo(
        () => ((customer && customer.companies) || []).map((c) => c.id),
        [customer]
    )

    const [runSearch, search] = useLazyQuery(COMPANY_TYPEAHEAD_QUERY, {
        fetchPolicy: 'network-only'
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useMemo(
        () => _.debounce((q) => { if (q.trim()) runSearch({ variables: { query: q.trim() } }) }, 350),
        [runSearch]
    )

    // Cancel any pending debounced search when the modal unmounts (destroyOnClose)
    // so it can't fire against a torn-down component.
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

    const options = useMemo(() => {
        const list = (search.data && search.data.searchCompanies) || []
        return list.map((c) => ({ value: c.id, label: companyLabel(c) }))
    }, [search.data])

    const [updateCustomer, updateState] = useMutation(UPDATE_CONTACT_COMPANIES_MUTATION)

    const reset = useCallback(() => {
        setMode(EXISTING)
        setSelected(null)
    }, [])

    const close = () => { reset(); onClose() }

    // Attach `companyId` to the customer by re-setting the union of their
    // current companies plus the new one. Returns true on success.
    const attachCompany = async (companyId) => {
        if (existingIds.includes(companyId)) {
            message.info('ลูกค้ารายนี้อยู่ในบริษัทนี้อยู่แล้ว')
            return false
        }
        try {
            const { data } = await updateCustomer({
                variables: {
                    customerNo,
                    customerParams: { customerCompanyIds: _.uniq([...existingIds, companyId]) }
                }
            })
            const payload = data && data.updateCustomer
            if (!payload) { message.error('ไม่สามารถเพิ่มบริษัทได้'); return false }
            if (payload.errors) { message.error(formatMutationErrors(payload.errors)); return false }
            message.success(`เพิ่มบริษัทให้ ${customer.fullName || customerNo} แล้ว`)
            reset()
            onSuccess(payload.customer)
            onClose()
            return true
        } catch (e) {
            // Network/GraphQL error already surfaces via updateState.error Alert.
            return false
        }
    }

    const handleAddExisting = () => { if (selected) attachCompany(selected) }
    // CompanyFormContainer's onSaveSuccess passes the newly-created company id.
    const handleCompanyCreated = (newCompanyId) => attachCompany(newCompanyId)

    return (
        <Modal
            title={`เพิ่มบริษัท${customer ? ` · ${customer.fullName || customerNo}` : ''}`}
            open={visible}
            onCancel={close}
            footer={false}
            destroyOnClose
            width={mode === NEW ? 720 : 560}
        >
            {updateState.error && (
                <Alert type="error" style={{ marginBottom: 16 }} message={updateState.error.message} />
            )}

            <Radio.Group
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                optionType="button"
                buttonStyle="solid"
                style={{ marginBottom: 20 }}
            >
                <Radio.Button value={EXISTING}>เลือกจากบริษัทที่มีอยู่</Radio.Button>
                <Radio.Button value={NEW}>สร้างบริษัทใหม่</Radio.Button>
            </Radio.Group>

            {mode === EXISTING ? (
                <div>
                    <Select
                        showSearch
                        allowClear
                        filterOption={false}
                        value={selected}
                        onSearch={debouncedSearch}
                        onChange={setSelected}
                        notFoundContent={search.loading ? 'กำลังค้นหา...' : 'พิมพ์เพื่อค้นหาบริษัท'}
                        placeholder="ค้นหาชื่อบริษัท, เลขภาษี"
                        options={options}
                        loading={search.loading}
                        style={{ width: '100%' }}
                    />
                    <div style={{ textAlign: 'right', marginTop: 20 }}>
                        <Button onClick={close} style={{ marginRight: 8 }}>ยกเลิก</Button>
                        <Button
                            type="primary"
                            disabled={!selected}
                            loading={updateState.loading}
                            onClick={handleAddExisting}
                        >
                            เพิ่มบริษัท
                        </Button>
                    </div>
                </div>
            ) : (
                // Reuse the real company-create form; on save we attach the new
                // company to this customer.
                <CompanyFormContainer
                    embedded
                    onSaveSuccess={handleCompanyCreated}
                    onCancel={close}
                />
            )}
        </Modal>
    )
}

AddCompanyModal.propTypes = {
    customer: PropTypes.object,
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func
}

AddCompanyModal.defaultProps = {
    customer: null,
    onSuccess: () => {}
}

export default AddCompanyModal
